import axiosInstance from '../../../services/axiosInstance';

export interface GenerationCapabilities {
  enabled: boolean;
  configured: boolean;
  maxSlides: number;
  maxSourceBytes: number;
  documents: boolean;
  workbooks: boolean;
  referenceImages: boolean;
}

export interface GenerationIssue {
  severity: 'blocker' | 'warning' | 'clarification';
  code: string;
  message: string;
  field?: string;
}

export interface GenerationRun {
  _id: string;
  status: string;
  clarification?: { code: string; message: string };
  issues?: GenerationIssue[];
  presentationId?: string | null;
  errorMessage?: string;
  sources?: Array<{ id: string; kind: string; fileName: string; sheets?: Array<{ name: string; rows: number; cols: number }> }>;
}

export async function getGenerationCapabilities(): Promise<GenerationCapabilities | null> {
  try {
    const { data } = await axiosInstance.get<GenerationCapabilities>('/api/presentations/generations/capabilities');
    return data;
  } catch {
    return null;
  }
}

export async function startGeneration(input: {
  prompt: string;
  name?: string;
  language?: string;
  market?: string;
  templateId?: string;
  articleNumbers?: string[];
  assetIds?: string[];
  deckCategoryId?: string | null;
  autoStart?: boolean;
}, idempotencyKey?: string): Promise<GenerationRun> {
  const { data } = await axiosInstance.post<GenerationRun>('/api/presentations/generations', input, {
    headers: { 'Idempotency-Key': idempotencyKey || `gen-${Date.now()}-${Math.random().toString(36).slice(2, 12)}` },
  });
  return data;
}

export async function searchGenerationProducts(
  query: string,
  page?: { skip?: number; limit?: number },
  signal?: AbortSignal,
): Promise<{ items: Array<{ _id: string; title: string; articleNumber?: string }> }> {
  const { data } = await axiosInstance.get('/api/presentations/generations/products', {
    params: { q: query, skip: page?.skip, limit: page?.limit || 8 },
    signal,
  });
  return data;
}

export async function resumeGeneration(runId: string): Promise<GenerationRun> {
  const { data } = await axiosInstance.post<GenerationRun>(`/api/presentations/generations/${runId}/start`);
  return data;
}

export async function getGeneration(runId: string, signal?: AbortSignal): Promise<GenerationRun> {
  const { data } = await axiosInstance.get<GenerationRun>(`/api/presentations/generations/${runId}`, { signal });
  return data;
}

export async function clarifyGeneration(runId: string, body: Record<string, unknown>): Promise<GenerationRun> {
  const { data } = await axiosInstance.post<GenerationRun>(`/api/presentations/generations/${runId}/clarify`, body);
  return data;
}

export async function cancelGeneration(runId: string): Promise<void> {
  await axiosInstance.post(`/api/presentations/generations/${runId}/cancel`);
}

export async function regenerateGeneration(runId: string): Promise<GenerationRun> {
  const { data } = await axiosInstance.post<GenerationRun>(`/api/presentations/generations/${runId}/regenerate`);
  return data;
}

export async function uploadGenerationSource(runId: string, file: File): Promise<GenerationRun> {
  const body = new FormData();
  body.append('file', file);
  const { data } = await axiosInstance.post<GenerationRun>(`/api/presentations/generations/${runId}/sources`, body, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export function pollGeneration(runId: string, onTick: (run: GenerationRun) => void, signal: AbortSignal): Promise<GenerationRun> {
  return new Promise((resolve, reject) => {
    const tick = async () => {
      if (signal.aborted) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }
      try {
        const run = await getGeneration(runId, signal);
        onTick(run);
        if (['completed', 'failed', 'cancelled', 'needs_clarification'].includes(run.status)) {
          resolve(run);
          return;
        }
        window.setTimeout(() => { void tick(); }, 1500);
      } catch (error) {
        reject(error);
      }
    };
    void tick();
  });
}
